import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { defer, EMPTY, Observable } from 'rxjs';
import { catchError, delay, map, retryWhen, take, tap } from 'rxjs/operators';
import { ApiModels } from '@mona/api';
import { TokenStorageService } from '@mona/auth';
import { ConfigService } from '@mona/config';
import { Encounter } from '@mona/models';
import { WsExtendableService } from '@mona/shared/utils';

/**
 * Websocket service for notifications
 */
@Injectable({ providedIn: 'root' })
export class NotificationsWsService extends WsExtendableService<ApiModels.Notification.Model> {
    /**
     * Constructor
     * @param configService ConfigService
     * @param tokenStorage
     */
    constructor(
        private configService: ConfigService,
        private tokenStorage: TokenStorageService,
    ) {
        super();
    }

    /**
     * Connects to ws for notifications
     * @param encounterId EntityId<Encounter>
     */
    connect(encounterId: EntityId<Encounter>): Observable<ApiModels.Notification.Model> {
        return defer(() => {
            this.disconnect();
            const accessToken = this.tokenStorage.getAccessToken();
            const url = `${this.configService.get('api.coreWsUrl')}/ws/notifications/${encounterId}/`;
            this.setConfig(
                url,
                accessToken,
                undefined,
                environment.reconnectInterval,
                // 0 = no base reconnect(); outer retryWhen + connectionReestablished own recovery
                0,
            );
            return super.connect();
        }).pipe(
            map((message: { type: string; notification: ApiModels.Notification }) =>
                ApiModels.Notification.DTO.toModel(message.notification),
            ),
            retryWhen(errors =>
                errors.pipe(
                    tap(error => console.warn('[ws] Notifications connect failed, retrying...', error)),
                    delay(environment.reconnectInterval),
                    take(environment.reconnectAttempts),
                ),
            ),
            catchError(() => EMPTY),
        );
    }
}
